import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { resolveNpsPrompt } from "@/hooks/useNpsPrompt";

interface NPSSurveyModalProps {
  isOpen: boolean;
  onClose: () => void;
  moduleName: string;
  /** Usuario autenticado (requerido para persistir la respuesta) */
  userId: string;
  /** Identificador único del disparador, ej. `challenge_lab_project:${projectId}` */
  triggerKey: string;
  /** Cohorte del usuario al momento de responder, o null si no tiene */
  cohortId: string | null;
}

export function NPSSurveyModal({ isOpen, onClose, moduleName, userId, triggerKey, cohortId }: NPSSurveyModalProps) {
  const [score, setScore] = useState<number | null>(null);
  const [feedback, setFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (score === null) {
      toast.error("Por favor selecciona una puntuación");
      return;
    }

    setIsSubmitting(true);
    try {
      await resolveNpsPrompt({ userId, triggerKey, cohortId, score, comment: feedback });
      toast.success("¡Gracias por tu feedback!");
      setScore(null);
      setFeedback("");
      onClose();
    } catch (err: any) {
      toast.error("No se pudo guardar", { description: err?.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getScoreColor = (value: number, selected: number | null) => {
    if (selected !== value) return "bg-muted hover:bg-muted/80 text-muted-foreground";
    if (value <= 6) return "bg-destructive text-destructive-foreground";
    if (value <= 8) return "bg-amber-500 text-white";
    return "bg-emerald-500 text-white";
  };

  const getScoreLabel = () => {
    if (score === null) return "";
    if (score <= 6) return "Detractor";
    if (score <= 8) return "Pasivo";
    return "Promotor";
  };

  const getScoreLabelColor = () => {
    if (score === null) return "text-muted-foreground";
    if (score <= 6) return "text-destructive";
    if (score <= 8) return "text-amber-500";
    return "text-emerald-500";
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl">¿Qué tan probable es que recomiendes esta ruta?</DialogTitle>
          <DialogDescription>
            Has completado <span className="font-semibold text-foreground">{moduleName}</span>. 
            Tu opinión nos ayuda a mejorar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Score selector */}
          <div className="space-y-4">
            <div className="flex justify-between text-xs text-muted-foreground px-1">
              <span>Nada probable</span>
              <span>Muy probable</span>
            </div>
            <div className="grid grid-cols-11 gap-1">
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
                <button
                  key={value}
                  onClick={() => setScore(value)}
                  className={cn(
                    "h-10 rounded-lg font-semibold text-sm transition-all duration-200",
                    "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    getScoreColor(value, score),
                    score === value && "ring-2 ring-ring ring-offset-2 scale-110"
                  )}
                >
                  {value}
                </button>
              ))}
            </div>
            {score !== null && (
              <p className={cn("text-center font-medium", getScoreLabelColor())}>
                {getScoreLabel()} ({score}/10)
              </p>
            )}
          </div>

          {/* Feedback textarea */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              ¿Qué podríamos mejorar? <span className="text-muted-foreground">(opcional)</span>
            </label>
            <Textarea
              placeholder="Comparte tus comentarios, sugerencias o lo que más te gustó..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Category hints based on score */}
          {score !== null && score <= 6 && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
              <p className="text-sm text-destructive">
                Lamentamos que tu experiencia no haya sido óptima. Tu feedback nos ayudará a mejorar.
              </p>
            </div>
          )}
        </div>

        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Más tarde
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || score === null}>
            {isSubmitting ? "Enviando..." : "Enviar feedback"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
